import type { CSSProperties, ReactNode } from 'react';
import type { Band, BantVerdict, Evidence, Severity } from '../api/types';

/* ------------------------------------------------------------------ *
 * Score and band
 * ------------------------------------------------------------------ */

export function toneForScore(score: number | null): string {
  if (score === null) return 'neutral';
  if (score >= 4.5) return 'excellent';
  if (score >= 3.5) return 'good';
  if (score >= 2.5) return 'warn';
  return 'poor';
}

export function toneForBand(band: Band | null): string {
  switch (band) {
    case 'Excellent':
      return 'excellent';
    case 'Good':
      return 'good';
    case 'Needs Work':
      return 'warn';
    case 'Poor':
      return 'poor';
    case 'FAIL':
      return 'fail';
    default:
      return 'neutral';
  }
}

export const SCORE_WORD: Record<number, string> = {
  5: 'Exemplary',
  4: 'Strong',
  3: 'Acceptable',
  2: 'Below Standard',
  1: 'Poor',
};

export function Score({ value, showMax = true }: { value: number | null; showMax?: boolean }) {
  if (value === null) return <span className="unavailable">N/A</span>;
  return (
    <span className={`score score-${toneForScore(value)}`}>
      <span className="score-num">{value.toFixed(1)}</span>
      {showMax && <span className="score-max">/5</span>}
    </span>
  );
}

export function BandPill({ band }: { band: Band | null }) {
  if (!band) return <span className="unavailable">Not scored</span>;
  return (
    <span className={`pill pill-${toneForBand(band)}`}>
      {band === 'FAIL' && <span className="dot" aria-hidden />}
      {band}
    </span>
  );
}

/** The 1-5 scale as five segments. Reads at a glance without being a chart. */
export function ScaleBar({ score }: { score: number | null }) {
  const tone = toneForScore(score);
  return (
    <span className="scale-bar" role="img" aria-label={score === null ? 'Not applicable' : `${score} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`scale-seg ${score !== null && n <= score ? `on-${tone}` : ''}`} />
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * BANT
 * ------------------------------------------------------------------ */

export function toneForVerdict(v: BantVerdict | string): string {
  switch (v) {
    case 'PASS':
      return 'excellent';
    case 'PARTIAL':
    case 'WEAK':
      return 'warn';
    case 'FAIL':
      return 'fail';
    default:
      return 'neutral';
  }
}

/**
 * Grey when the factor was never established (nothing to judge yet), yellow
 * when it was established but falls short (PARTIAL/WEAK), green when it
 * passes, red on an outright FAIL. `evidenceUnavailable` takes priority over
 * the verdict, since a PARTIAL raised only because nothing was said is not
 * the same "weak" as a PARTIAL raised over conflicting evidence.
 */
export function toneForBant(v: BantVerdict | string, evidenceUnavailable?: boolean): string {
  if (evidenceUnavailable) return 'neutral';
  return toneForVerdict(v);
}

export function VerdictPill({ verdict, size }: { verdict: BantVerdict | string; size?: 'sm' }) {
  return <span className={`pill pill-${toneForVerdict(verdict)} ${size === 'sm' ? 'pill-sm' : ''}`}>{verdict}</span>;
}

/** The compact B/A/N/T strip used in the calls table. */
export function BantStrip({ factors }: { factors: { verdict: string; evidenceUnavailable: boolean }[] }) {
  if (!factors.length) return <span className="unavailable">—</span>;
  const letters = ['B', 'A', 'N', 'T'];
  return (
    <span className="row" style={{ gap: 3 }}>
      {factors.map((f, i) => (
        <span
          key={letters[i] ?? i}
          className={`pill pill-sm pill-${toneForBant(f.verdict, f.evidenceUnavailable)}`}
          title={`${['Budget', 'Authority', 'Need', 'Time frame'][i]}: ${f.evidenceUnavailable ? 'Not established' : f.verdict}`}
        >
          {letters[i]}
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Severity
 * ------------------------------------------------------------------ */

export function SeverityPill({ severity }: { severity: Severity }) {
  const tone = severity === 'high' ? 'fail' : severity === 'med' ? 'warn' : 'neutral';
  return <span className={`pill pill-${tone}`}>{severity.toUpperCase()}</span>;
}

/* ------------------------------------------------------------------ *
 * Evidence
 *
 * Clicking evidence jumps to the transcript. Every finding in the app that
 * has a quote routes through this component, so the behaviour is identical
 * everywhere the user meets it.
 * ------------------------------------------------------------------ */

export function EvidenceQuote({
  evidence,
  onJump,
  label,
}: {
  evidence: Evidence;
  onJump?: (segmentIdx: number | null, quote: string) => void;
  label?: string;
}) {
  const clickable = Boolean(onJump);
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      className="evidence"
      {...(clickable ? { type: 'button' as const, onClick: () => onJump!(evidence.segmentIdx, evidence.quote) } : {})}
    >
      <span className="evidence-meta">
        {evidence.timestamp && <span className="evidence-time">{evidence.timestamp}</span>}
        <span className="evidence-speaker">{evidence.speaker}</span>
        {label && <span className="dim small">{label}</span>}
        {clickable && <span className="evidence-jump">Jump to transcript →</span>}
      </span>
      <span className="evidence-quote">&ldquo;{evidence.quote}&rdquo;</span>
      {evidence.matchMethod === 'ngram' && (
        <span className="evidence-loose">
          Partial match: this quote was matched on a run of words, not verbatim. Check it against the recording.
        </span>
      )}
    </Tag>
  );
}

export function EvidenceList({
  evidence,
  onJump,
  emptyMessage = 'Evidence unavailable — no verified quote supports this finding.',
}: {
  evidence: Evidence[];
  onJump?: (segmentIdx: number | null, quote: string) => void;
  emptyMessage?: string;
}) {
  if (!evidence.length) return <div className="evidence-empty">{emptyMessage}</div>;
  return (
    <div className="stack" style={{ gap: 7 }}>
      {evidence.map((e, i) => (
        <EvidenceQuote key={`${e.segmentIdx}-${i}`} evidence={e} onJump={onJump} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Provenance
 *
 * Requirement: it must be clear which results came from the transcript, call
 * metadata, SDR-entered data, external data, AI analysis, or application logic.
 * ------------------------------------------------------------------ */

const PROVENANCE_LABEL: Record<string, string> = {
  transcript: 'Transcript',
  call_metadata: 'Call metadata',
  sdr_reported: 'SDR-entered',
  external_data: 'External data',
  ai_analysis: 'AI analysis',
  deterministic: 'Application rule',
};

export function ProvenanceTags({ provenance }: { provenance: string[] }) {
  if (!provenance.length) return null;
  return (
    <span className="row row-wrap" style={{ gap: 4 }}>
      {provenance.map((p) => (
        <span key={p} className="pill pill-sm" title={`Source of this finding: ${PROVENANCE_LABEL[p] ?? p}`}>
          {PROVENANCE_LABEL[p] ?? p}
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Layout helpers
 * ------------------------------------------------------------------ */

export function Card({
  title,
  actions,
  note,
  children,
  bodyless,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
  bodyless?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {note && <span className="card-note">{note}</span>}
          {actions && <div className="card-head-actions">{actions}</div>}
        </header>
      )}
      {bodyless ? children : <div className="card-body">{children}</div>}
    </section>
  );
}

export function Stat({
  label,
  value,
  meta,
  alert,
  hint,
  index = 0,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  alert?: boolean;
  hint?: string;
  /** Position in the grid; drives the staggered entry delay. */
  index?: number;
}) {
  return (
    <div className={`stat ${alert ? 'is-alert' : ''}`} style={{ '--i': index } as CSSProperties}>
      <div className="stat-label" title={hint}>
        {label}
      </div>
      <div className={`stat-value ${value === null || value === undefined ? 'is-none' : ''}`}>{value}</div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-art" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 3.5h12a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1Z" />
          <path d="M6.5 7.5h7M6.5 10.5h7M6.5 13.5h4" />
        </svg>
      </div>
      <div className="empty-title">{title}</div>
      {body && <div className="empty-body">{body}</div>}
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%' }: { height?: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} />;
}

export function ErrorNote({ error, onRetry }: { error: { message: string } | null; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <div className="alert row" role="alert">
      <span>{error.message}</span>
      {onRetry && (
        <button type="button" className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Icons — inline so there is no icon-font request and they inherit colour.
 * ------------------------------------------------------------------ */

const icon = (path: ReactNode) => (props: { size?: number }) => (
  <svg
    width={props.size ?? 15}
    height={props.size ?? 15}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {path}
  </svg>
);

export const IconDashboard = icon(
  <>
    <rect x="2" y="2" width="5" height="5" rx="1" />
    <rect x="9" y="2" width="5" height="5" rx="1" />
    <rect x="2" y="9" width="5" height="5" rx="1" />
    <rect x="9" y="9" width="5" height="5" rx="1" />
  </>,
);
export const IconCalls = icon(
  <>
    <path d="M2 4h12M2 8h12M2 12h8" />
  </>,
);
export const IconUpload = icon(
  <>
    <path d="M8 11V3M5 6l3-3 3 3" />
    <path d="M2.5 10v2.5a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V10" />
  </>,
);
export const IconRubric = icon(
  <>
    <path d="M3.5 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z" />
    <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
  </>,
);
export const IconCoaching = icon(<path d="M8 2.2 9.8 5.9l4.1.6-3 2.9.7 4.1L8 11.6l-3.6 1.9.7-4.1-3-2.9 4.1-.6L8 2.2Z" />);
export const IconChevron = icon(<path d="M6 3.5 10.5 8 6 12.5" />);
export const IconSearch = icon(
  <>
    <circle cx="7" cy="7" r="4.5" />
    <path d="m10.5 10.5 3 3" />
  </>,
);
